import { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCompact, formatMoney } from '@/lib/utils/format';
import { useImpactStats } from './api';

function useCountUp(target: number, active: boolean, durationMs = 1200): number {
  const [value, setValue] = useState(0);
  const raf = useRef<number>(0);

  useEffect(() => {
    if (!active) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setValue(target);
      return;
    }
    let start: number | null = null;
    const step = (ts: number) => {
      start ??= ts;
      const progress = Math.min((ts - start) / durationMs, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active, durationMs]);

  return value;
}

export function ImpactStats() {
  const { data, isPending, isError } = useImpactStats();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const raised = useCountUp(data?.total_raised_minor ?? 0, visible && !!data);
  const donors = useCountUp(data?.donors_count ?? 0, visible && !!data);
  const donations = useCountUp(data?.donations_count ?? 0, visible && !!data);

  if (isError) return null; // impact band is non-critical; hide on error

  return (
    <div ref={ref} className="grid grid-cols-1 gap-6 sm:grid-cols-3">
      {isPending ? (
        Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)
      ) : (
        <>
          <Stat value={formatMoney(raised, data.currency)} label="Raised for men's care" />
          <Stat value={formatCompact(donors)} label="Generous supporters" />
          <Stat value={formatCompact(donations)} label="Donations made" />
        </>
      )}
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-[var(--radius-card)] bg-surface p-6 text-center shadow-[var(--shadow-soft)]">
      <div className="text-3xl font-bold text-purple-600 md:text-4xl">{value}</div>
      <div className="mt-1 text-sm text-muted">{label}</div>
    </div>
  );
}
